import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Users,
    Clock,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    RefreshCw,
    Calendar,
    DollarSign,
    Timer,
    UserCheck,
    UserX
} from 'lucide-react';
import { GABON_TIMEZONE, getGabonNow } from '../utils/gabonTime';

const TodayAttendancePage = () => {
    const { api, hasPermission, formatCurrency } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [selectedService, setSelectedService] = useState('all');
    const [services, setServices] = useState([]);
    const [canSeeAmounts, setCanSeeAmounts] = useState(false);
    
    // Validate absence dialog
    const [showValidateDialog, setShowValidateDialog] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [absenceReason, setAbsenceReason] = useState('');
    const [absenceType, setAbsenceType] = useState('ABSENT');
    
    const canValidateAbsence = hasPermission('validate_absence');

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const params = selectedService !== 'all' ? `?service=${selectedService}` : '';
            const [attendanceRes, servicesRes] = await Promise.all([
                api().get(`/attendance/today-detailed${params}`),
                api().get('/services')
            ]);
            setData(attendanceRes.data);
            setCanSeeAmounts(attendanceRes.data.can_see_amounts || false);
            setServices(servicesRes.data.services || []);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    }, [api, selectedService]);

    useEffect(() => {
        fetchData();
        // Real-time polling every 10 seconds
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const handleValidateAbsence = async () => {
        if (!selectedRecord || !absenceReason.trim()) {
            toast.error('Motif requis');
            return;
        }
        
        try {
            await api().post(`/attendance/${selectedRecord.attendance_id}/validate-absence`, {
                reason: absenceReason,
                absence_type: absenceType
            });
            toast.success('Absence validee');
            setShowValidateDialog(false);
            setSelectedRecord(null);
            setAbsenceReason('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur de validation');
        }
    };

    const formatTime = (isoString) => {
        if (!isoString) return '--:--';
        const date = new Date(isoString);
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    const getStatusBadge = (status) => {
        const configs = {
            'PRESENT': { color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
            'LATE': { color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
            'ABSENT': { color: 'bg-red-100 text-red-700', icon: XCircle },
            'NOT_CLOCKED': { color: 'bg-red-100 text-red-700', icon: UserX },
            'PENDING': { color: 'bg-gray-100 text-gray-700', icon: Clock }
        };
        const config = configs[status] || configs['PENDING'];
        const Icon = config.icon;
        return (
            <Badge className={`${config.color} gap-1`}>
                <Icon className="w-3 h-3" />
                {status === 'PRESENT' ? 'Present' : 
                 status === 'LATE' ? 'Retard' : 
                 status === 'ABSENT' ? 'Absent' : 
                 status === 'NOT_CLOCKED' ? 'Non pointe' : 'En attente'}
            </Badge>
        );
    };

    if (loading && !data) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-64"></div>
                <div className="grid grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-xl"></div>)}
                </div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    const stats = data?.stats || {};

    return (
        <div className="space-y-6" data-testid="today-attendance-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Presence du jour
                    </h1>
                    <p className="text-muted-foreground mt-1 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {getGabonNow().toLocaleDateString('fr-FR', { timeZone: GABON_TIMEZONE, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={selectedService} onValueChange={setSelectedService}>
                        <SelectTrigger className="w-36">
                            <SelectValue placeholder="Service" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous services</SelectItem>
                            {services.map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={fetchData} data-testid="refresh-btn">
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className={`grid grid-cols-2 ${canSeeAmounts ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-4`}>
                <Card className="bg-gradient-to-br from-accent/10 to-accent/5">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Planifies</p>
                                <p className="text-2xl font-bold">{stats.total_scheduled || 0}</p>
                            </div>
                            <Users className="w-8 h-8 text-accent" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-green-50">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Presents</p>
                                <p className="text-2xl font-bold text-green-600">{stats.present_count || 0}</p>
                            </div>
                            <CheckCircle2 className="w-8 h-8 text-green-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-orange-50">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Retards</p>
                                <p className="text-2xl font-bold text-orange-600">{stats.late_count || 0}</p>
                            </div>
                            <AlertTriangle className="w-8 h-8 text-orange-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-red-50">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Absents/Non pointes</p>
                                <p className="text-2xl font-bold text-red-600">{(stats.absent_count || 0) + (stats.not_clocked_count || 0)}</p>
                            </div>
                            <XCircle className="w-8 h-8 text-red-500" />
                        </div>
                    </CardContent>
                </Card>
                {/* Retenues jour - ONLY for Direction */}
                {canSeeAmounts && (
                    <Card className="bg-yellow-50">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Retenues jour</p>
                                    <p className="text-lg font-bold text-yellow-700">{stats.total_late_penalty_formatted || '0 CFA'}</p>
                                </div>
                                <DollarSign className="w-8 h-8 text-yellow-600" />
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Retards Section */}
            {data?.late?.length > 0 && (
                <Card className="border-orange-200 bg-orange-50/30">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2 text-orange-700">
                            <AlertTriangle className="w-5 h-5" />
                            Retards ({data.late.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Employe</TableHead>
                                    <TableHead>Service</TableHead>
                                    <TableHead>Poste</TableHead>
                                    <TableHead className="text-center">Prevu</TableHead>
                                    <TableHead className="text-center">Arrive</TableHead>
                                    <TableHead className="text-center">Retard</TableHead>
                                    {canSeeAmounts && <TableHead className="text-center">Paliers</TableHead>}
                                    {canSeeAmounts && <TableHead className="text-right">Retenue</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.late.map(record => (
                                    <TableRow key={record.attendance_id} className="bg-orange-50/50">
                                        <TableCell className="font-medium">{record.staff_name}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{record.service}</Badge>
                                        </TableCell>
                                        <TableCell>{record.position}</TableCell>
                                        <TableCell className="text-center font-mono">{record.scheduled_start}</TableCell>
                                        <TableCell className="text-center font-mono text-orange-600">
                                            {formatTime(record.check_in_time)}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge className="bg-orange-100 text-orange-700">
                                                +{record.late_minutes} min
                                            </Badge>
                                        </TableCell>
                                        {canSeeAmounts && (
                                            <TableCell className="text-center">
                                                <Badge variant="outline" className="bg-orange-50">
                                                    {record.late_penalty_hours}h
                                                </Badge>
                                            </TableCell>
                                        )}
                                        {canSeeAmounts && (
                                            <TableCell className="text-right font-mono font-semibold text-red-600">
                                                {record.late_penalty_formatted}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Presents Section */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <UserCheck className="w-5 h-5 text-green-600" />
                        Presents ({data?.presents?.length || 0})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {!data?.presents?.length ? (
                        <p className="text-center text-muted-foreground py-8">
                            Aucun employe present pour le moment
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Employe</TableHead>
                                    <TableHead>Service</TableHead>
                                    <TableHead>Poste</TableHead>
                                    <TableHead className="text-center">Prevu</TableHead>
                                    <TableHead className="text-center">Arrive</TableHead>
                                    <TableHead className="text-center">Statut</TableHead>
                                    <TableHead className="text-center">Retard</TableHead>
                                    {canSeeAmounts && <TableHead className="text-right">Retenue</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.presents.map(record => (
                                    <TableRow key={record.attendance_id}>
                                        <TableCell className="font-medium">{record.staff_name}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{record.service}</Badge>
                                        </TableCell>
                                        <TableCell>{record.position}</TableCell>
                                        <TableCell className="text-center font-mono">{record.scheduled_start}</TableCell>
                                        <TableCell className="text-center font-mono">
                                            {formatTime(record.check_in_time)}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {getStatusBadge(record.status)}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {record.late_minutes > 0 ? (
                                                <Badge className="bg-orange-100 text-orange-700">
                                                    +{record.late_minutes} min
                                                </Badge>
                                            ) : '-'}
                                        </TableCell>
                                        {canSeeAmounts && (
                                            <TableCell className="text-right font-mono">
                                                {record.late_penalty_amount > 0 ? (
                                                    <span className="text-red-600">{record.late_penalty_formatted}</span>
                                                ) : '-'}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Absents / Non Pointes Section */}
            <Card className="border-red-200 bg-red-50/30">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2 text-red-700">
                        <UserX className="w-5 h-5" />
                        Absents / Non pointes ({data?.absent_not_clocked?.length || 0})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {!data?.absent_not_clocked?.length ? (
                        <p className="text-center text-muted-foreground py-8">
                            Aucun absent ni non pointe
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Employe</TableHead>
                                    <TableHead>Service</TableHead>
                                    <TableHead>Poste</TableHead>
                                    <TableHead className="text-center">Heure prevue</TableHead>
                                    <TableHead className="text-center">Statut</TableHead>
                                    <TableHead className="text-center">Motif</TableHead>
                                    {canValidateAbsence && <TableHead className="text-center">Action</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.absent_not_clocked.map((record, idx) => (
                                    <TableRow key={record.schedule_id || idx} className="bg-red-50/50">
                                        <TableCell className="font-medium">{record.staff_name}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline">{record.service}</Badge>
                                        </TableCell>
                                        <TableCell>{record.position}</TableCell>
                                        <TableCell className="text-center font-mono">{record.scheduled_start}</TableCell>
                                        <TableCell className="text-center">
                                            {getStatusBadge(record.status)}
                                        </TableCell>
                                        <TableCell className="text-center text-sm">
                                            {record.reason || '-'}
                                        </TableCell>
                                        {canValidateAbsence && (
                                            <TableCell className="text-center">
                                                {record.status !== 'ABSENT' && record.attendance_id && (
                                                    <Button 
                                                        size="sm" 
                                                        variant="outline"
                                                        className="text-red-600 border-red-200 hover:bg-red-50"
                                                        onClick={() => {
                                                            setSelectedRecord(record);
                                                            setShowValidateDialog(true);
                                                        }}
                                                    >
                                                        Valider absence
                                                    </Button>
                                                )}
                                                {record.status === 'ABSENT' && (
                                                    <Badge className="bg-red-100 text-red-700">Valide</Badge>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Validate Absence Dialog */}
            <Dialog open={showValidateDialog} onOpenChange={setShowValidateDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Valider une absence</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="p-3 bg-muted rounded-lg">
                            <p className="font-medium">{selectedRecord?.staff_name}</p>
                            <p className="text-sm text-muted-foreground">
                                {selectedRecord?.service} - {selectedRecord?.position}
                            </p>
                            <p className="text-sm text-muted-foreground">
                                Shift prevu: {selectedRecord?.scheduled_start}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>Type d'absence</Label>
                            <Select value={absenceType} onValueChange={setAbsenceType}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ABSENT">Absent non justifie</SelectItem>
                                    <SelectItem value="SICK">Malade</SelectItem>
                                    <SelectItem value="LEAVE">Conge</SelectItem>
                                    <SelectItem value="OTHER">Autre</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Motif (obligatoire)</Label>
                            <Textarea
                                value={absenceReason}
                                onChange={(e) => setAbsenceReason(e.target.value)}
                                placeholder="Entrez le motif de l'absence..."
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowValidateDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleValidateAbsence}
                            className="bg-red-600 hover:bg-red-700"
                            disabled={!absenceReason.trim()}
                        >
                            Confirmer absence
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TodayAttendancePage;
